import { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useSettings } from '@/hooks/useSettings';
import { UserSettings } from '@/types';
import {
  GeneralSection,
  RitualSection,
  DisplaySection,
  DeepWorkSection,
  AccountSection,
  ConfirmDialog,
  containerVariants,
  sectionVariants,
} from './settings-components';
import styles from './Settings.module.css';

// ============================================
// Types & Constants
// ============================================

interface SettingsProps {
  onBack?: () => void;
}

const DURATION_OPTIONS = [
  { label: '15 min', value: 15 },
  { label: '30 min', value: 30 },
  { label: '45 min', value: 45 },
  { label: '60 min', value: 60 },
];

const START_HOUR_OPTIONS = [
  { label: '5:00 AM', value: 5 },
  { label: '6:00 AM', value: 6 },
  { label: '7:00 AM', value: 7 },
  { label: '8:00 AM', value: 8 },
  { label: '9:00 AM', value: 9 },
];

const REMINDER_OPTIONS = [
  { label: '10 min', value: 10 },
  { label: '15 min', value: 15 },
  { label: '30 min', value: 30 },
  { label: '45 min', value: 45 },
];

const THEME_OPTIONS = [
  { label: 'Light', value: 'light' as const },
  { label: 'Dark', value: 'dark' as const },
];

// ============================================
// Main Component
// ============================================

export function Settings({ onBack }: SettingsProps) {
  const { settings, user, updateSingleSetting, signOut } = useSettings();
  const [showSignOutDialog, setShowSignOutDialog] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);

  // Handlers
  const handleBack = useCallback(() => {
    if (onBack) {
      onBack();
    } else {
      window.history.back();
    }
  }, [onBack]);

  const handleSettingChange = useCallback(
    <K extends keyof UserSettings>(key: K, value: UserSettings[K]) => {
      updateSingleSetting(key, value);
    },
    [updateSingleSetting]
  );

  const handleSignOut = useCallback(async () => {
    setIsSigningOut(true);
    try {
      await signOut();
    } catch (error) {
      console.error('Failed to sign out:', error);
      setIsSigningOut(false);
    }
  }, [signOut]);

  return (
    <div className={styles.container}>
      {/* Header */}
      <header className={styles.header}>
        <motion.button
          type="button"
          className={styles.backBtn}
          onClick={handleBack}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Go back"
        >
          {'<-'}
        </motion.button>
        <motion.h1
          className={styles.title}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.2 }}
        >
          Settings
        </motion.h1>
      </header>

      {/* Settings Sections */}
      <motion.div
        className={styles.content}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <GeneralSection
          theme={settings.theme}
          defaultBlockDuration={settings.defaultBlockDuration}
          defaultStartHour={settings.defaultStartHour}
          themeOptions={THEME_OPTIONS}
          durationOptions={DURATION_OPTIONS}
          startHourOptions={START_HOUR_OPTIONS}
          onThemeChange={(value: UserSettings['theme']) => handleSettingChange('theme', value)}
          onDurationChange={(value: number) => handleSettingChange('defaultBlockDuration', value)}
          onStartHourChange={(value: number) => handleSettingChange('defaultStartHour', value)}
        />

        <RitualSection
          shutdownTime={settings.shutdownTime}
          notifications={settings.notifications}
          onShutdownTimeChange={(value: string) => handleSettingChange('shutdownTime', value)}
          onNotificationsChange={(value: boolean) => handleSettingChange('notifications', value)}
        />

        <DisplaySection
          showRevisionMarks={settings.showRevisionMarks}
          sketchyLineStyle={settings.sketchyLineStyle}
          onShowRevisionMarksChange={(value: boolean) =>
            handleSettingChange('showRevisionMarks', value)
          }
          onSketchyLineStyleChange={(value: boolean) =>
            handleSettingChange('sketchyLineStyle', value)
          }
        />

        <DeepWorkSection
          fixedScheduleEnabled={settings.fixedScheduleEnabled}
          fixedScheduleEndTime={settings.fixedScheduleEndTime}
          fixedScheduleReminderMinutes={settings.fixedScheduleReminderMinutes}
          showDeepWorkMetrics={settings.showDeepWorkMetrics}
          weeklyPlanningEnabled={settings.weeklyPlanningEnabled}
          dayThemingEnabled={settings.dayThemingEnabled}
          reminderOptions={REMINDER_OPTIONS}
          onFixedScheduleEnabledChange={(value: boolean) =>
            handleSettingChange('fixedScheduleEnabled', value)
          }
          onFixedScheduleEndTimeChange={(value: string) =>
            handleSettingChange('fixedScheduleEndTime', value)
          }
          onFixedScheduleReminderMinutesChange={(value: number) =>
            handleSettingChange('fixedScheduleReminderMinutes', value)
          }
          onShowDeepWorkMetricsChange={(value: boolean) =>
            handleSettingChange('showDeepWorkMetrics', value)
          }
          onWeeklyPlanningEnabledChange={(value: boolean) =>
            handleSettingChange('weeklyPlanningEnabled', value)
          }
          onDayThemingEnabledChange={(value: boolean) =>
            handleSettingChange('dayThemingEnabled', value)
          }
        />

        <AccountSection
          email={user?.email}
          isSigningOut={isSigningOut}
          onSignOutClick={() => setShowSignOutDialog(true)}
        />

        {/* Footer */}
        <motion.footer className={styles.footer} variants={sectionVariants}>
          <p className={styles.footerText}>Time Blocking</p>
          <p className={styles.footerQuote}>
            "A 40 hour time-blocked work week produces the same amount of output as a 60+ hour work
            week pursued without structure."
          </p>
        </motion.footer>
      </motion.div>

      {/* Sign Out Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showSignOutDialog}
        title="Sign Out"
        message="Are you sure you want to sign out? You'll need to log in again to access your time blocks."
        confirmLabel="Sign Out"
        onConfirm={() => {
          setShowSignOutDialog(false);
          handleSignOut();
        }}
        onCancel={() => setShowSignOutDialog(false)}
      />
    </div>
  );
}

export default Settings;
